import { z } from 'zod';

import type { RoutePaths } from 'router/types';

import type { Lead } from 'providers/LeadProvider';

/**
 * Business types the `/start/$businessType` template page accepts, in the exact
 * spelling used both by the URL segment and by `Lead['businessType']` (the URL
 * segment matches the business-type key verbatim, so there is no slug lookup
 * table to keep in sync).
 *
 * `personal_trainer` is deliberately absent: it is not a UI variant of this
 * page but a different submit action entirely (no lead is created, the user is
 * redirected to Trainiac), so it is never reachable through this route — see
 * `docs/adr/014-shared-lead-submission-hook-personal-trainer-as-caller-guard.md`.
 */
export const START_BUSINESS_TYPES = [
  'gym',
  'studio',
  'wellness_center',
] as const satisfies ReadonlyArray<NonNullable<Lead['businessType']>>;

export type StartBusinessType = (typeof START_BUSINESS_TYPES)[number];

export const startBusinessTypeSchema = z.enum(START_BUSINESS_TYPES);

/**
 * The route pattern exactly as registered in `routeTree.gen` — `satisfies
 * RoutePaths` breaks this module the moment the route is renamed or removed,
 * instead of letting the pathname types below drift into fiction.
 */
const START_BUSINESS_TYPE_ROUTE = '/start/$businessType' satisfies RoutePaths;

type StartBusinessTypeRoutePrefix =
  typeof START_BUSINESS_TYPE_ROUTE extends `${infer Prefix}$businessType`
    ? Prefix
    : never;

export type StartBusinessTypePathname =
  `${StartBusinessTypeRoutePrefix}${StartBusinessType}`;

/**
 * The concrete pathnames `/start/$businessType` resolves to at runtime.
 *
 * `routePathsToStepName` and friends are keyed by the route *pattern*
 * (`/start/$businessType`), which never matches `location.pathname` for a
 * dynamic route, so the step machinery needs the resolved paths as well. Built
 * from `START_BUSINESS_TYPES`, validated against the pattern-derived prefix,
 * so neither the types nor the route can drift.
 */
export const START_BUSINESS_TYPE_PATHNAMES = START_BUSINESS_TYPES.map(
  businessType => `/start/${businessType}` as const,
) satisfies StartBusinessTypePathname[];

/** Type guard for the resolved `/start/*` pathnames. */
export function isStartBusinessTypePathname(
  pathname: string | null,
): pathname is StartBusinessTypePathname {
  return pathname !== null && START_BUSINESS_TYPE_PATHNAMES.includes(pathname);
}
